import { initTelemetry, reportWorkflow } from "./observability/telemetry.js";
import AppErrorBoundary from "./observability/AppErrorBoundary.jsx";
import ReactDOM from "react-dom/client";
import "bootstrap/dist/css/bootstrap.css";
import "./index.css";
import App from "./App.jsx";
import { BrowserRouter } from "react-router-dom";
import { EventType, PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import {
  clearRememberedActiveMsalAccount,
  enableMsalAccountStorageEvents,
  internalMsalConfig,
  msalConfig,
  registerMsalInstances,
  resolveActiveMsalAccount,
  setActiveMsalAccount,
} from "./authConfig.js";
import { axiosInstance as axios } from "./Components/axiosInstance.js";
import { attachAuthInterceptors } from "./Components/authAxios.jsx";
import { initAnalytics, trackEvent } from "./analytics/ga4.js";

initTelemetry();

const msalInstance = new PublicClientApplication(msalConfig);
const internalMsalInstance = new PublicClientApplication(internalMsalConfig);
registerMsalInstances(msalInstance, internalMsalInstance);

const root = ReactDOM.createRoot(document.getElementById("root"));

axios.defaults.headers.common["Cache-Control"] = "no-cache";
axios.defaults.headers.common.Pragma = "no-cache";
initAnalytics();

function registerMsalEventCallback(instance) {
  instance.addEventCallback((event) => {
    if (
      (event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
      event.payload?.account
    ) {
      setActiveMsalAccount(instance, event.payload.account);

      if (event.eventType === EventType.LOGIN_SUCCESS) {
        trackEvent("login_succeeded", {
          workflow_area: "auth",
          is_authenticated: true,
          result: "success",
        });
      }

      return;
    }

    if (event.eventType === EventType.LOGOUT_SUCCESS) {
      clearRememberedActiveMsalAccount();
    }
  });
}

registerMsalEventCallback(msalInstance);
registerMsalEventCallback(internalMsalInstance);

async function completePendingRedirects() {
  // MsalProvider only owns the external instance. Complete both here so a
  // workforce redirect is handled by its own app registration before render.
  // MSAL ignores redirect state owned by a different client ID and memoizes
  // repeated calls, so MsalProvider can safely complete its normal startup too.
  await Promise.all([
    msalInstance.handleRedirectPromise(),
    internalMsalInstance.handleRedirectPromise(),
  ]);
}

Promise.all([msalInstance.initialize(), internalMsalInstance.initialize()]).then(async () => {
  enableMsalAccountStorageEvents();
  await completePendingRedirects();

  // Accounts are visible across both instances (shared localStorage cache, account entries aren't
  // clientId-scoped) -- resolving against the external instance's account list is enough regardless of
  // which instance a user actually signed in through.
  resolveActiveMsalAccount(msalInstance);

  attachAuthInterceptors(msalInstance);

  root.render(
    <AppErrorBoundary>
    <MsalProvider instance={msalInstance}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </MsalProvider>
    </AppErrorBoundary>
  );
}).catch(() => {
  reportWorkflow({ workflow: "bootstrap", outcome: "failed", reason: "bootstrap_failed" });
  root.render(<main role="alert" className="p-6"><h1>Sign-in could not start</h1><p>Your saved records are retained. Reload to try again.</p><button onClick={() => window.location.reload()}>Retry sign-in</button></main>);
});
